import React from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemBody,
  AccordionItemHeader,
  Alert,
  AlertTriangleIcon,
  Amount,
  Badge,
  Box,
  Button,
  CheckCircle2Icon,
  ClockIcon,
  Drawer,
  DrawerBody,
  DrawerHeader,
  EditIcon,
  ExternalLinkIcon,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ProgressBar,
  RefreshIcon,
  Skeleton,
  StepGroup,
  StepItem,
  StepItemIndicator,
  SubscriptionsIcon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  XCircleIcon,
  useToast,
} from '@razorpay/blade/components';
import { EmptyView } from 'components/SalaryComponents/components/EmptyView';
import { DetailsSkeleton } from 'components/ui/Skeletons/DetailsSkeleton';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import {
  BADGE_TO_ICON_COLOR,
  EMI_STATUS_COLOR,
  EMI_STATUS_LABEL,
  EMPLOYEE_LOAN_CATEGORIES_KEY,
  FETCH_MY_LOANS,
  LOAN_DETAIL_KEY,
  LOAN_STATUS,
  LOAN_STATUS_COLOR,
  LOAN_STATUS_LABEL,
  RECOVERY_PREVIEW_KEY,
} from './constants';
import { formatDate, formatMonthYear } from './utils';
import { LoanDetail, LoanDetailDrawerProps, RawEntry, ScheduleEntry } from './types';
import { LoanCategory } from 'components/Settings/EmployeeLoansSettings/types';
import { CreateOrEditLoanRequest } from './CreateOrEditLoanRequest';
import { INTEREST_TYPE_LABEL } from 'components/LoanPaymentV2/constants';

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center">
    <Text size="small" variant="body" weight="medium" color="surface.text.gray.muted">
      {label}
    </Text>
    <Text size="small" weight="semibold" color="surface.text.gray.subtle" textAlign="right">
      {value}
    </Text>
  </Box>
);

const StatusIcon = ({ status }: { status: number }) => {
  const badgeColor = LOAN_STATUS_COLOR[status] ?? 'neutral';
  const iconColor = BADGE_TO_ICON_COLOR[badgeColor] ?? 'surface.icon.gray.normal';

  if (status === LOAN_STATUS.CLOSED) {
    return <CheckCircle2Icon color={iconColor as any} size="2xlarge" />;
  }
  if (status === LOAN_STATUS.REJECTED) {
    return <XCircleIcon color={iconColor as any} size="2xlarge" />;
  }
  if (status === LOAN_STATUS.ACTIVE) {
    return <SubscriptionsIcon color={iconColor as any} size="2xlarge" />;
  }
  return <ClockIcon color={iconColor as any} size="2xlarge" />;
};

export const LoanDetailDrawer = ({ isOpen, onDismiss, loanId }: LoanDetailDrawerProps) => {
  const queryClient = useQueryClient();
  const { show: showToast } = useToast();
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = React.useState(false);
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const activeLoanIdRef = React.useRef<number | null>(null);
  const activeLoanRef = React.useRef<LoanDetail | null>(null);
  if (loanId) activeLoanIdRef.current = loanId;

  const {
    data: rawLoanData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [LOAN_DETAIL_KEY, loanId],
    queryFn: () => api.loans.getLoanDetail(activeLoanIdRef.current!),
    enabled: !!loanId && isOpen,
  });

  const loanPayload = rawLoanData as LoanDetail[] | { data?: LoanDetail[] } | undefined;
  const freshLoan: LoanDetail | undefined = Array.isArray(loanPayload)
    ? loanPayload[0]
    : (loanPayload as { data?: LoanDetail[] } | undefined)?.data?.[0];

  if (freshLoan) activeLoanRef.current = freshLoan;
  const loan: LoanDetail | null = freshLoan ?? (!loanId ? activeLoanRef.current : null);

  const needsSchedule =
    loan &&
    (loan.status === LOAN_STATUS.APPROVED ||
      loan.status === LOAN_STATUS.ACTIVE ||
      loan.status === LOAN_STATUS.DISBURSAL_IN_PROGRESS);

  const { data: rawRecovery, isFetching: isScheduleFetching } = useQuery({
    queryKey: [RECOVERY_PREVIEW_KEY, loanId],
    queryFn: () => api.loans.recoveryPreview(loanId!),
    enabled: !!loanId && !!needsSchedule,
  });

  const isDrawerLoading = isLoading || (!!needsSchedule && isScheduleFetching);

  const recoveryPayload = rawRecovery as
    | { data?: RawEntry[] | { recovery_schedule?: RawEntry[] } }
    | undefined;
  const rawScheduleData = recoveryPayload?.data;
  const schedule: ScheduleEntry[] = (
    Array.isArray(rawScheduleData)
      ? rawScheduleData
      : (rawScheduleData as { recovery_schedule?: RawEntry[] } | undefined)?.recovery_schedule ?? []
  ).map((entry, idx) => ({ ...entry, id: entry.id ?? idx }));

  const { data: rawCategoryData } = useQuery({
    queryKey: [EMPLOYEE_LOAN_CATEGORIES_KEY],
    queryFn: () => api.loans.listEmployeeLoanCategories(),
    enabled: !!loan?.loan_category_id && loan?.status === LOAN_STATUS.PENDING,
    staleTime: 5 * 60 * 1000,
  });
  const categoryName =
    (rawCategoryData as { data?: LoanCategory[] } | undefined)?.data?.find(
      (c) => c.id === loan?.loan_category_id,
    )?.name ?? null;

  const downloadDocumentMutation = useMutation({
    mutationFn: (fileId: string) => api.loans.downloadLoanDocument(fileId),
    onSuccess: (res) => {
      window.open(res.signed_url, '_blank');
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to open document. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: () => api.loans.withdrawLoan(activeLoanIdRef.current!),
    onSuccess: () => {
      queryClient.invalidateQueries(FETCH_MY_LOANS);
      setIsWithdrawModalOpen(false);
      onDismiss();
      showToast({
        content: 'Loan request withdrawn successfully.',
        color: 'positive',
        duration: 4000,
      });
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to withdraw request. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  return (
    <>
      <Drawer isOpen={isOpen} onDismiss={onDismiss}>
        {!isDrawerLoading ? (
          <DrawerHeader
            title={loan ? LOAN_STATUS_LABEL[loan.status] ?? 'Loan Details' : 'Loan Details'}
            color={LOAN_STATUS_COLOR[loan?.status ?? -1] ?? 'neutral'}>
            {loan ? (
              <Box
                display="flex"
                flexDirection="column"
                gap="spacing.4"
                paddingTop="spacing.5"
                width="100%">
                <Box display="flex" flexDirection="column" alignItems="center" gap="spacing.4">
                  <StatusIcon status={loan.status} />
                  <Amount value={loan.principal} size="2xlarge" type="heading" weight="semibold" />
                  <Badge color={LOAN_STATUS_COLOR[loan.status] ?? 'neutral'} size="large">
                    {LOAN_STATUS_LABEL[loan.status] ?? 'Unknown'}
                  </Badge>
                  {loan.status === LOAN_STATUS.PENDING && (
                    <Box display="flex" gap="spacing.3" width="100%">
                      <Button
                        variant="primary"
                        isFullWidth
                        icon={EditIcon}
                        onClick={() => {
                          onDismiss();
                          setIsEditOpen(true);
                        }}>
                        Edit Request
                      </Button>
                      <Button
                        variant="secondary"
                        color="negative"
                        isFullWidth
                        onClick={() => {
                          onDismiss();
                          setIsWithdrawModalOpen(true);
                        }}>
                        Withdraw Request
                      </Button>
                    </Box>
                  )}
                  {loan.status === LOAN_STATUS.ACTIVE && (
                    <Box display="flex" flexDirection="column" gap="spacing.4" width="100%">
                      <Box display="flex" justifyContent="space-between">
                        <Box
                          paddingLeft="spacing.3"
                          borderLeftWidth="thick"
                          borderLeftColor="surface.border.gray.muted">
                          <Text
                            size="medium"
                            variant="body"
                            weight="medium"
                            color="surface.text.gray.muted">
                            Repaid Amount
                          </Text>
                          <Amount value={loan.amount_paid ?? 0} size="medium" />
                        </Box>
                        <Box
                          textAlign="left"
                          paddingLeft="spacing.3"
                          borderLeftWidth="thick"
                          borderLeftColor="surface.border.gray.muted">
                          <Text
                            size="medium"
                            variant="body"
                            weight="medium"
                            color="surface.text.gray.muted">
                            Outstanding Amount
                          </Text>
                          <Amount value={loan.remaining_amount_to_be_paid ?? 0} size="medium" />
                        </Box>
                      </Box>
                      <ProgressBar
                        accessibilityLabel="Repayment progress"
                        value={
                          loan.total_amount
                            ? Math.round(((loan.amount_paid ?? 0) / loan.total_amount) * 100)
                            : 0
                        }
                      />
                    </Box>
                  )}
                  {loan.status === LOAN_STATUS.CLOSED && (
                    <Text
                      size="medium"
                      weight="semibold"
                      color="surface.text.gray.normal"
                      textAlign="center">
                      You've successfully repaid and closed this advance 🎉
                    </Text>
                  )}
                </Box>
                {loan.status === LOAN_STATUS.REJECTED && loan.rejection_note && (
                  <Alert
                    color="negative"
                    isDismissible={false}
                    description={`Reason: ${loan.rejection_note}`}
                    icon={AlertTriangleIcon}
                    isFullWidth
                  />
                )}
                <Text
                  size="small"
                  variant="body"
                  weight="medium"
                  color={
                    loan.status === LOAN_STATUS.ACTIVE
                      ? 'surface.text.gray.subtle'
                      : 'surface.text.gray.muted'
                  }
                  textAlign="center">
                  {loan.status === LOAN_STATUS.ACTIVE
                    ? `${loan.duration_completed ?? 0} of ${loan.tenure} EMI(s) paid`
                    : `#${loan.id} • Requested on ${formatDate(loan.created_at)}${
                        loan.status === LOAN_STATUS.CLOSED && loan.updated_at
                          ? ` • Closed on ${formatDate(loan.updated_at)}`
                          : ''
                      }`}
                </Text>
              </Box>
            ) : undefined}
          </DrawerHeader>
        ) : null}

        <DrawerBody>
          {isDrawerLoading && (
            <Box
              paddingX="spacing.4"
              paddingY="spacing.3"
              display="flex"
              flexDirection="column"
              gap="spacing.6">
              <Box display="flex" flexDirection="column" gap="spacing.2">
                <Skeleton height="spacing.7" />
              </Box>
              <DetailsSkeleton rowCount={6} />
            </Box>
          )}
          {isError && (
            <EmptyView
              isError
              description={
                <Text color="surface.text.gray.muted" weight="medium" textAlign="center">
                  Could not load loan details. Please try again.
                </Text>
              }>
              <Box display="flex" justifyContent="center">
                <Button icon={RefreshIcon} size="small" onClick={() => refetch()}>
                  Retry
                </Button>
              </Box>
            </EmptyView>
          )}

          {loan && !isDrawerLoading && (
            <Box display="flex" flexDirection="column" gap="spacing.5">
              {(loan.status === LOAN_STATUS.PENDING ||
                loan.status === LOAN_STATUS.APPROVED ||
                loan.status === LOAN_STATUS.REJECTED ||
                loan.status === LOAN_STATUS.DISBURSAL_IN_PROGRESS) && (
                <Box backgroundColor="surface.background.gray.moderate">
                  <Accordion defaultExpandedIndex={0}>
                    <AccordionItem>
                      <AccordionItemHeader title="Timeline" />
                      <AccordionItemBody>
                        {(() => {
                          const isDisbursed =
                            loan.status === LOAN_STATUS.APPROVED ||
                            loan.status === LOAN_STATUS.DISBURSAL_IN_PROGRESS;
                          const isRejected = loan.status === LOAN_STATUS.REJECTED;
                          const steps: React.ReactElement[] = [];

                          if (!isRejected) {
                            steps.push(
                              <StepItem
                                key="disbursement"
                                title="Disbursement of Amount"
                                description="By Admin or Finance"
                                marker={
                                  <StepItemIndicator color={isDisbursed ? 'primary' : 'neutral'} />
                                }
                                stepProgress={isDisbursed ? 'start' : 'none'}
                              />,
                            );
                          }

                          steps.push(
                            <StepItem
                              key="approval"
                              title="Request Approval"
                              description="By Admin"
                              marker={
                                <StepItemIndicator
                                  color={
                                    isRejected ? 'negative' : isDisbursed ? 'positive' : 'neutral'
                                  }
                                />
                              }
                              stepProgress={isDisbursed ? 'full' : 'none'}
                            />,
                          );

                          steps.push(
                            <StepItem
                              key="submitted"
                              title="Request Submitted"
                              description="By You"
                              marker={<StepItemIndicator color="positive" />}
                              stepProgress="full"
                            />,
                          );

                          return (
                            <StepGroup orientation="vertical" size="medium">
                              {steps}
                            </StepGroup>
                          );
                        })()}
                      </AccordionItemBody>
                    </AccordionItem>
                  </Accordion>
                </Box>
              )}

              {needsSchedule && schedule.length > 0 && (
                <Box backgroundColor="surface.background.gray.moderate">
                  <Accordion defaultExpandedIndex={0}>
                    <AccordionItem>
                      <AccordionItemHeader title="Repayment Schedule" />
                      <AccordionItemBody>
                        <Table<ScheduleEntry>
                          data={{ nodes: schedule }}
                          gridTemplateColumns={
                            loan.status === LOAN_STATUS.ACTIVE ? '1fr 1fr 1fr' : '1fr 1fr'
                          }>
                          {(tableData) => (
                            <>
                              <TableHeader>
                                <TableHeaderRow>
                                  <TableHeaderCell>Month</TableHeaderCell>
                                  <TableHeaderCell textAlign="right">EMI</TableHeaderCell>
                                  {loan.status === LOAN_STATUS.ACTIVE && (
                                    <TableHeaderCell>Status</TableHeaderCell>
                                  )}
                                </TableHeaderRow>
                              </TableHeader>
                              <TableBody>
                                {tableData.map((entry) => (
                                  <TableRow key={entry.id} item={entry}>
                                    <TableCell>
                                      <Text size="small" color="surface.text.gray.subtle">
                                        {formatMonthYear(entry.payroll_month ?? entry.month)}
                                      </Text>
                                    </TableCell>
                                    <TableCell textAlign="right">
                                      <Amount
                                        value={entry.emi_amount ?? entry.total_emi ?? 0}
                                        size="small"
                                      />
                                    </TableCell>
                                    {loan.status === LOAN_STATUS.ACTIVE && (
                                      <TableCell>
                                        <Badge
                                          color={EMI_STATUS_COLOR[entry.status ?? -1] ?? 'neutral'}
                                          size="small">
                                          {EMI_STATUS_LABEL[entry.status ?? -1] ?? 'Pending'}
                                        </Badge>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </>
                          )}
                        </Table>
                      </AccordionItemBody>
                    </AccordionItem>
                  </Accordion>
                </Box>
              )}

              {(loan.status === LOAN_STATUS.APPROVED ||
                loan.status === LOAN_STATUS.DISBURSAL_IN_PROGRESS ||
                loan.status === LOAN_STATUS.ACTIVE ||
                loan.status === LOAN_STATUS.CLOSED) && (
                <Box backgroundColor="surface.background.gray.moderate">
                  <Accordion defaultExpandedIndex={0}>
                    <AccordionItem>
                      <AccordionItemHeader
                        title={
                          loan.status === LOAN_STATUS.APPROVED ||
                          loan.status === LOAN_STATUS.DISBURSAL_IN_PROGRESS
                            ? 'Approved Details'
                            : 'Details'
                        }
                      />
                      <AccordionItemBody>
                        {loan.status === LOAN_STATUS.ACTIVE && (
                          <DetailRow label="Loan ID" value={`#${loan.id}`} />
                        )}
                        <DetailRow
                          label="Amount"
                          value={
                            loan.principal != null ? (
                              <Amount value={loan.principal} size="small" />
                            ) : (
                              '—'
                            )
                          }
                        />
                        <DetailRow
                          label="Repayment Tenure"
                          value={loan.tenure ? `${loan.tenure} Months` : '—'}
                        />
                        <DetailRow
                          label="EMI Start Month"
                          value={
                            loan.recovery_start_from
                              ? formatMonthYear(loan.recovery_start_from)
                              : '—'
                          }
                        />
                        <DetailRow
                          label="Interest Type"
                          value={
                            loan.interest_type != null
                              ? INTEREST_TYPE_LABEL[loan.interest_type] ?? '—'
                              : '—'
                          }
                        />
                        <DetailRow
                          label="Rate of Interest"
                          value={loan.interest_rate != null ? `${loan.interest_rate}%` : '—'}
                        />
                        <DetailRow
                          label="Perquisite"
                          value={loan.perk_rate != null ? `${loan.perk_rate}%` : '—'}
                        />
                        <DetailRow
                          label="Monthly EMI"
                          value={
                            loan.monthly_total_emi != null ? (
                              <Amount value={loan.monthly_total_emi} size="small" />
                            ) : (
                              '—'
                            )
                          }
                        />
                      </AccordionItemBody>
                    </AccordionItem>
                  </Accordion>
                </Box>
              )}
              {loan.status === LOAN_STATUS.PENDING && (
                <Box backgroundColor="surface.background.gray.moderate">
                  <Accordion defaultExpandedIndex={0}>
                    <AccordionItem>
                      <AccordionItemHeader title="Request Details" />
                      <AccordionItemBody>
                        <DetailRow
                          label="Amount"
                          value={
                            loan.principal != null ? (
                              <Amount value={loan.principal} size="small" />
                            ) : (
                              '—'
                            )
                          }
                        />
                        <DetailRow label="Category" value={categoryName ?? '—'} />
                        <DetailRow label="Reason" value={loan.remarks ?? '—'} />
                        <DetailRow
                          label="Tenure"
                          value={loan.tenure ? `${loan.tenure} Months` : '—'}
                        />
                        <DetailRow
                          label="EMI Start Month"
                          value={formatMonthYear(loan.recovery_start_from)}
                        />
                        {loan.document_file_id && (
                          <DetailRow
                            label="Documents"
                            value={
                              <Link
                                icon={ExternalLinkIcon}
                                iconPosition="left"
                                onClick={() =>
                                  downloadDocumentMutation.mutate(loan.document_file_id!)
                                }
                                size="small">
                                {downloadDocumentMutation.isLoading ? 'Loading...' : 'Attachment'}
                              </Link>
                            }
                          />
                        )}
                      </AccordionItemBody>
                    </AccordionItem>
                  </Accordion>
                </Box>
              )}

              {(loan.status === LOAN_STATUS.REJECTED ||
                loan.status === LOAN_STATUS.DISBURSAL_IN_PROGRESS) &&
                (() => {
                  const rows = [
                    <DetailRow
                      key="amount"
                      label="Amount"
                      value={
                        loan.principal != null ? (
                          <Amount value={loan.principal} size="small" />
                        ) : (
                          '—'
                        )
                      }
                    />,
                    <DetailRow key="id" label="Request ID" value={`#${loan.id}`} />,
                    <DetailRow
                      key="date"
                      label="Requested on"
                      value={formatDate(loan.created_at)}
                    />,
                    <DetailRow
                      key="reason"
                      label="Reason for Request"
                      value={loan.loan_category_name ?? '—'}
                    />,
                    <DetailRow key="desc" label="Specific Reason" value={loan.remarks} />,
                    loan.document_file_id && (
                      <DetailRow
                        key="docs"
                        label="Documents"
                        value={
                          <Link
                            icon={ExternalLinkIcon}
                            iconPosition="left"
                            onClick={() => downloadDocumentMutation.mutate(loan.document_file_id!)}
                            isDisabled={downloadDocumentMutation.isLoading}
                            size="small">
                            {downloadDocumentMutation.isLoading ? 'Loading…' : 'View Document'}
                          </Link>
                        }
                      />
                    ),
                    <DetailRow
                      key="tenure"
                      label="Repayment Tenure"
                      value={loan.tenure ? `${loan.tenure} Months` : '—'}
                    />,
                    <DetailRow
                      key="emi-start"
                      label="Deduction Start Month"
                      value={formatMonthYear(loan.recovery_start_from)}
                    />,
                  ].filter(Boolean);

                  return (
                    <Box backgroundColor="surface.background.gray.moderate">
                      <Accordion defaultExpandedIndex={0}>
                        <AccordionItem>
                          <AccordionItemHeader title="Request Details" />
                          <AccordionItemBody>{rows}</AccordionItemBody>
                        </AccordionItem>
                      </Accordion>
                    </Box>
                  );
                })()}
              {loan.status === LOAN_STATUS.PENDING && (
                <Alert
                  color="information"
                  isDismissible={false}
                  description="Final amount, tenure, deduction start month and interest rates are subject to approval."
                />
              )}
            </Box>
          )}
        </DrawerBody>
      </Drawer>

      <Modal
        isOpen={isWithdrawModalOpen}
        onDismiss={() => setIsWithdrawModalOpen(false)}
        size="small">
        <ModalHeader title="Withdraw Request?" />
        <ModalBody>
          <Text size="medium" variant="body" weight="regular" color="surface.text.gray.normal">
            Are you sure you want to withdraw this request? This action will permanently delete it.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Box display="flex" gap="spacing.4" width="100%">
            <Button variant="secondary" isFullWidth onClick={() => setIsWithdrawModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="negative"
              isFullWidth
              isLoading={withdrawMutation.isLoading}
              onClick={() => withdrawMutation.mutate()}>
              Withdraw Now
            </Button>
          </Box>
        </ModalFooter>
      </Modal>
      {loan && (
        <CreateOrEditLoanRequest
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          editLoan={{
            id: loan.id,
            principal: loan.principal,
            tenure: loan.tenure,
            recoveryStartFrom: loan.recovery_start_from ?? '',
            description: loan.remarks ?? '',
            loanCategoryId: loan.loan_category_id ?? undefined,
            documentFileId: loan.document_file_id ?? undefined,
          }}
        />
      )}
    </>
  );
};
